import { computed } from 'vue';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { getClickoutUrlContextsArray, trackClickout } from '@/helpers/clickout-helper';
import type { ClickoutUrl } from '@/helpers/clickout-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import { MonetizationType } from '@/@types/graphql-types';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { useRentalModal } from '@/features/jwtv-titles-rental/composables/useRentalModal';
import { openJwTvAvodModal } from '@/features/jwtv-avod/helpers/jwtv-avod-helper';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import { type MaybeRefOrGetter, toValue } from '@vueuse/core';

type OfferWithPackage = {
	package: {
		shortName: string;
		technicalName: string;
	};
	monetizationType: MonetizationType;
	streamUrl?: string | null;
};

const MODAL_PROVIDER_SHORT_NAMES = ['jwt', 'otp', 'pxp', 'brp'];

const playerModalByProvider: Record<string, { cmp: () => Promise<unknown> }> = {
	plexplayer: {
		cmp: () => import('@/ui-components/video-player-modals/PlexPlayerModal.vue'),
	},
	ottstudioplayer: {
		cmp: () => import('@/ui-components/video-player-modals/OttPlayerModal.vue'),
	},
	brollieplayer: {
		cmp: () => import('@/ui-components/video-player-modals/BrolliePlayerModal.vue'),
	},
};

export function usePlayerModal(shortName: MaybeRefOrGetter<string>) {
	const { openRentalModal } = useRentalModal();

	const isModalProvider = computed(() => MODAL_PROVIDER_SHORT_NAMES.includes(toValue(shortName)));

	function openThirdPartyPlayerModal({
		offer,
		title,
		clickoutUrl,
	}: {
		offer: OfferWithPackage;
		title?: TitleDetail;
		clickoutUrl: ClickoutUrl;
	}): void {
		const modalData = playerModalByProvider[offer.package.technicalName];
		ModalHelper.openModal(
			modalData.cmp,
			{
				offer,
				title,
				additionalContexts: getClickoutUrlContextsArray(clickoutUrl),
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
	}

	async function openPlayerModal({
		offer,
		title,
		clickoutUrl,
		rentalOptions,
	}: {
		offer: TitleOfferFragment | WatchNowOfferFragment;
		title?: TitleDetail;
		clickoutUrl: ClickoutUrl;
		rentalOptions?: {
			forceSafeOpen?: boolean;
			overrideRedirectUrl?: boolean;
		};
	}): Promise<void> {
		trackClickout(clickoutUrl);

		if (isJWTVRentalOffer(offer)) {
			openRentalModal({
				offer: offer,
				title: title!,
				clickoutUrl,
				forceSafeOpen: rentalOptions?.forceSafeOpen,
				overrideRedirectUrl: rentalOptions?.overrideRedirectUrl ?? true,
			});
			return;
		}

		if (offer.package.shortName === 'jwt') {
			await openJwTvAvodModal({
				modalProps: {
					offer: offer,
					title,
					additionalContexts: getClickoutUrlContextsArray(clickoutUrl),
				},
			});
		} else {
			openThirdPartyPlayerModal({ offer, title, clickoutUrl });
		}
	}

	return { isModalProvider, openPlayerModal };
}
